import { isPlatformBrowser } from '@angular/common';
import { Inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Angulartics2 } from 'angulartics2';
import { Angulartics2GoogleTagManager } from 'angulartics2/gtm';
import identity from 'lodash.identity';
import pickBy from 'lodash.pickby';
import { Observable, of, Subject } from 'rxjs';
import { catchError, map, take } from 'rxjs/operators';
import { USER_TYPE_KEY } from '~app/core/modules/identity/constants/identity.constant';
import { UrlUtils } from '~app/modules/generic/utils/url.utils';
import { AccountInfoService } from '~core/modules/identity/services/account-info.service';
import { IdentityService } from '~core/modules/identity/services/identity.service';
import { IUserTypeLogInInfo } from '~shared/models/user-type-login-info';
import { ApplicationEvent } from '~shared/modules/application-event/models';
import { ApplicationEventService } from '~shared/modules/application-event/services/application-event.service';
import { GtmUserTypeEnum } from '~shared/modules/ga-tracking/constants';
import { GATrackingEventOptions } from '~shared/modules/ga-tracking/models/ga-tracking-event-options.model';
import {
  GATrackingApplicationEventMapperUtil,
  GATrackingApplicationEventValidator,
} from '~shared/modules/ga-tracking/utils';
import { GtmDatalayerService } from '~shared/services/gtm-datalayer.service';
import { PageDetectionUtil } from '~shared/utils/page-detection.util';
import { GATrackingEvent, GATrackingEventPayload } from '../models';
import { GaTrackingCheckoutService } from '../utils/ga-tracking-checkout.service';
import { METRO_COOKIE_NAME } from '~shared/constants/buyer-cookie.constant';
import { CookieService } from '~shared/services/cookie.service';

@Injectable({
  providedIn: 'root',
})
export class GATrackingService {
  public isTrackingStarted$: Subject<boolean> = new Subject();
  private eventsBatch: any = [];

  constructor(
    private angulartics2GoogleTagManager: Angulartics2GoogleTagManager,
    private angulartics2: Angulartics2,
    private applicationEventService: ApplicationEventService,
    private identityService: IdentityService,
    private gtmDataLayer: GtmDatalayerService,
    private accountInfoService: AccountInfoService,
    @Inject(PLATFORM_ID) private platformId: Object,
    private cookieService: CookieService
  ) {}

  public startTracking(): void {
    this.gtmDataLayer.initDataLayer();
    this.angulartics2GoogleTagManager.startTracking();
    this.subscribeToApplicationEvents();
    this.isTrackingStarted$.next(true);
    this.isTrackingStarted$.complete();
  }

  public getItemListName(url: string): string {
    if (PageDetectionUtil.isLandingPagePage(url)) {
      return 'landing_page';
    }

    if (UrlUtils.isCatalogPage(url)) {
      return 'catalog';
    }

    if (UrlUtils.isCategoryPage(url)) {
      return 'category';
    }

    if (UrlUtils.isSearchPage(url)) {
      return 'search';
    }

    return '';
  }

  public reportPendingGAEvents(): void {
    this.eventsBatch.forEach((event) => {
      this.track(event);
    });
    this.eventsBatch = [];
  }

  private subscribeToApplicationEvents(): void {
    this.applicationEventService.subscribe().subscribe((event: ApplicationEvent) => {
      this.onApplicationEvent(event);
    });
  }

  private onApplicationEvent(event: ApplicationEvent): void {
    if (!GATrackingApplicationEventValidator.isValid(event)) {
      return;
    }
    const isCookieAccepted = this.cookieService.get(METRO_COOKIE_NAME)?.includes?.('|performance|');

    const events: GATrackingEvent | GATrackingEvent[] = GATrackingApplicationEventMapperUtil.map(event);

    if (!isCookieAccepted) {
      this.eventsBatch.push(events);
      return;
    }

    if (Array.isArray(events)) {
      events.forEach((gaEvent) => this.track(gaEvent));
    } else {
      this.track(events);
    }
  }

  private getTierInfo(): Observable<string> {
    return this.accountInfoService.getAccountInfo().pipe(
      take(1),
      map(({ tier }) => tier || GtmUserTypeEnum.NotLoggedId),
      catchError((error) => of(GtmUserTypeEnum.NotLoggedId))
    );
  }

  private triggerEvent(
    event: GATrackingEvent,
    userInfo: IUserTypeLogInInfo,
    payload: Partial<GATrackingEventPayload>,
    options?: Partial<GATrackingEventOptions>,
    isEmployee = isPlatformBrowser(this.platformId) && localStorage.getItem(USER_TYPE_KEY) === 'EMP'
  ) {
    const regularGaEvent = {
      action: typeof event.getEventType === 'function' ? event.getEventType() : null,
      properties: {
        ...payload,
        gtmCustom: {
          'content-name': window.location.pathname + window.location.search,
          ...payload.gtmCustom,
          ...(isEmployee && payload.gtmCustom?.action ? { action: `emp - ${payload.gtmCustom.action}` } : {}),
          ...(options.sendCustomerType ? { customerType: userInfo.registrationType } : {}),
          loggedIn: userInfo.loggedIn,
          ...this.gtmDataLayer.getDataLayerConfig(event),
        },
      },
    };

    if (event?.isGA4) {
      const ga4Event = this.mapToGA4Event(event);
      this.trackGA4ECommerceCleaning(ga4Event);
      this.gtmDataLayer.pushDataLayer(ga4Event);

      return;
    }

    if (GaTrackingCheckoutService.isCheckoutGA4Event(event)) {
      const ga4Event = {
        ...GaTrackingCheckoutService.convertGaToGA4GtagEvent(regularGaEvent),
        ...this.gtmDataLayer.getDataLayerConfig(event),
      };
      this.trackGA4ECommerceCleaning(ga4Event);
      this.gtmDataLayer.pushDataLayer(ga4Event);

      return;
    }

    this.trackECommerceCleaning(payload);

    this.angulartics2.eventTrack.next(regularGaEvent);
  }

  private track(event: GATrackingEvent): void {
    if (event) {
      const userInfo = this.identityService.getUserInformation();
      const payload = pickBy(event.getPayload(), identity);
      const options = event.getOptions ? event.getOptions() : {};

      this.getTierInfo().subscribe((tier: string) => {
        event.tier = tier;
        this.triggerEvent(event, userInfo, payload, options);
      });
    }

  }

  private trackECommerceCleaning(payload: Partial<GATrackingEventPayload>) {
    if (payload.gtmCustom?.ecommerce == undefined) {
      return;
    }

    this.gtmDataLayer.gtagECommerceClearEvent();
  }

  private trackGA4ECommerceCleaning(payload: { ecommerce: unknown }) {
    if (payload.ecommerce == undefined) {
      return;
    }

    this.gtmDataLayer.gtagECommerceClearEvent();
  }

  private mapToGA4Event(event: GATrackingEvent) {
    const eventPayload: GATrackingEventPayload = event.getPayload();
    const items = eventPayload.gtmCustom?.items || eventPayload.gtmCustom?.ecommerce?.items;
    return {
      event: eventPayload?.event,
      ...this.gtmDataLayer.getDataLayerConfig(event),
      ecommerce: {
        ...eventPayload.gtmCustom?.ecommerce,
        items,
      },
    };
  }
}
